import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { adminBannersAPI, adminBannerCreateAPI, adminBannerUpdateAPI, adminBannerDeleteAPI } from '../../api/admin';
import { Card, Field, Input, Toggle, Btn, Row, Grid, Badge, Empty, Msg } from './ui';
import tokens from '../theme/tokens';

// 이미지 배너 — 메인 슬라이더(PC 이미지 필수 · 모바일 이미지 선택 · 링크 · 순서 · 노출). 위 폼에서 추가/수정, 아래 목록
const useStyles = makeStyles((theme) => ({
  drop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
    borderRadius: 12,
    border: `1px dashed ${tokens.line}`,
    backgroundColor: tokens.surfaceSoft,
    color: tokens.muted,
    fontSize: 13,
    cursor: 'pointer',
    overflow: 'hidden',
    '&:hover': { borderColor: tokens.primary, color: tokens.primaryDark },
    '& img': { display: 'block', maxWidth: '100%', maxHeight: 180, objectFit: 'contain' },
  },
  list: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14, [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' } },
  item: {
    borderRadius: 14,
    border: `1px solid ${tokens.lineSoft}`,
    overflow: 'hidden',
    backgroundColor: tokens.surface,
  },
  thumb: {
    display: 'block',
    width: '100%',
    height: 140,
    objectFit: 'cover',
    backgroundColor: tokens.surfaceSoft,
  },
  body: { padding: '12px 14px' },
  title: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', '& b': { fontSize: 14, color: tokens.ink, letterSpacing: -0.6 } },
  meta: { marginTop: 4, fontSize: 12, color: tokens.muted, letterSpacing: -0.4, wordBreak: 'break-all' },
  actions: { display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' },
}));

const emptyForm = { id: '', title: '', subTitle: '', linkUrl: '', sort: 0, isActive: true };

function AdminBanners() {
  const cs = useStyles();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [image, setImage] = useState(null);
  const [mobile, setMobile] = useState(null);
  const [preview, setPreview] = useState({ image: '', mobile: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ ok: false, text: '' });
  const imgRef = useRef(null);
  const mobRef = useRef(null);

  const load = async () => {
    const resp = await adminBannersAPI();
    if (resp && resp.status === 200) setRows(resp.data.rows || []);
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const pick = (which) => (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (which === 'image') { setImage(file); setPreview((p) => ({ ...p, image: url })); }
    else { setMobile(file); setPreview((p) => ({ ...p, mobile: url })); }
  };
  const reset = () => {
    setForm(emptyForm); setImage(null); setMobile(null); setPreview({ image: '', mobile: '' });
    if (imgRef.current) imgRef.current.value = '';
    if (mobRef.current) mobRef.current.value = '';
  };
  const edit = (b) => {
    setForm({ id: b.id, title: b.title, subTitle: b.subTitle, linkUrl: b.linkUrl, sort: b.sort, isActive: b.isActive });
    setImage(null); setMobile(null);
    setPreview({ image: b.imageUrl, mobile: b.mobileImageUrl });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const save = async () => {
    if (!form.id && !image) { setMsg({ ok: false, text: 'PC 배너 이미지를 올려 주세요.' }); return; }
    setBusy(true);
    setMsg({ ok: false, text: '' });
    const fd = new FormData();
    ['title', 'subTitle', 'linkUrl', 'sort'].forEach((k) => fd.append(k, form[k]));
    fd.append('isActive', form.isActive ? 'true' : 'false');
    if (image) fd.append('image', image);
    if (mobile) fd.append('mobileImage', mobile);
    const resp = form.id ? await adminBannerUpdateAPI(form.id, fd) : await adminBannerCreateAPI(fd);
    setBusy(false);
    if (resp && (resp.status === 200 || resp.status === 201)) { setMsg({ ok: true, text: '저장했습니다.' }); reset(); load(); }
    else setMsg({ ok: false, text: (resp && resp.data && resp.data.message) || '저장하지 못했습니다.' });
  };

  const toggleActive = async (b) => {
    const fd = new FormData();
    fd.append('isActive', b.isActive ? 'false' : 'true');
    const resp = await adminBannerUpdateAPI(b.id, fd);
    if (resp && resp.status === 200) setRows((prev) => prev.map((r) => (r.id === b.id ? resp.data.banner : r)));
  };
  const remove = async (b) => {
    if (!window.confirm(`'${b.title || '배너'}'를 삭제할까요?`)) return;
    const resp = await adminBannerDeleteAPI(b.id);
    if (resp && resp.status === 200) setRows((prev) => prev.filter((r) => r.id !== b.id));
  };

  return (
    <React.Fragment>
      <Card title={form.id ? '배너 수정' : '새 배너'} desc="PC 1436×auto 권장 · 모바일은 없으면 PC 이미지를 쓴다" action={form.id && <Btn tone="ghost" size="sm" onClick={reset}>새로 만들기</Btn>}>
        <Grid cols={2}>
          <Field label="PC 이미지">
            <div className={cs.drop} onClick={() => imgRef.current && imgRef.current.click()}>
              {preview.image ? <img src={preview.image} alt="" /> : '이미지 선택'}
            </div>
            <input ref={imgRef} type="file" accept="image/*" hidden onChange={pick('image')} />
          </Field>
          <Field label="모바일 이미지 (선택)">
            <div className={cs.drop} onClick={() => mobRef.current && mobRef.current.click()}>
              {preview.mobile ? <img src={preview.mobile} alt="" /> : '이미지 선택'}
            </div>
            <input ref={mobRef} type="file" accept="image/*" hidden onChange={pick('mobile')} />
          </Field>
        </Grid>
        <Grid cols={2}>
          <Field label="제목"><Input value={form.title} onChange={set('title')} /></Field>
          <Field label="부제"><Input value={form.subTitle} onChange={set('subTitle')} /></Field>
        </Grid>
        <Grid cols={2}>
          <Field label="클릭 링크 (선택)"><Input value={form.linkUrl} onChange={set('linkUrl')} placeholder="https://" /></Field>
          <Field label="순서"><Input type="number" value={form.sort} onChange={set('sort')} /></Field>
        </Grid>
        <Row>
          <Toggle checked={form.isActive} onChange={set('isActive')} label="노출" />
          <Btn onClick={save} disabled={busy}>{busy ? '저장 중' : form.id ? '수정 저장' : '배너 추가'}</Btn>
          <Msg ok={msg.ok}>{msg.text}</Msg>
        </Row>
      </Card>
      <Card title="배너 목록" desc={`${rows.length}개`}>
        {!rows.length && <Empty>등록된 배너가 없습니다.</Empty>}
        <div className={cs.list}>
          {rows.map((b) => (
            <div key={b.id} className={cs.item}>
              <img className={cs.thumb} src={b.imageUrl} alt="" />
              <div className={cs.body}>
                <div className={cs.title}><b>{b.title || '(제목 없음)'}</b><Badge tone={b.isActive ? 'on' : 'off'}>{b.isActive ? '노출' : '숨김'}</Badge></div>
                <div className={cs.meta}>순서 {b.sort}{b.mobileImageUrl ? ' · 모바일 이미지 있음' : ''}{b.linkUrl ? ` · ${b.linkUrl}` : ''}</div>
                <div className={cs.actions}>
                  <Toggle checked={b.isActive} onChange={() => toggleActive(b)} label="노출" />
                  <Btn tone="line" size="sm" onClick={() => edit(b)}>수정</Btn>
                  <Btn tone="danger" size="sm" onClick={() => remove(b)}>삭제</Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </React.Fragment>
  );
}

export default AdminBanners;
